
import { Vue, Options } from "vue-class-component";

import {AssociatedParty, OfferAssociatedParty, Person, Third} from "@/types";
import store from "@/store";
import { useRoute } from "vue-router";
import { globalConfig, UON, upsertWithDataMessageReturn, bankAccountStatusOption, addExpandedItem, removeExpandedItem } from "@/utils";
import { formatDateLocale } from "@/utils/configuration/formatters-config";

@Options({
  props: ["party", "tabPosition","role"],
  watch: {
    "$store.state.middleOfficeModule.section"(newValue) {
      this.showAccountExpense = newValue === `section-account${this.tabPosition}`;
    },
    accountsModel: {
      handler() {
        this.syncPreferredFromRoleAndPreleved();
      },
      deep: true,
      immediate: true
    },
    "role.partyAccount.resourceUid": {
      handler() {
        this.syncPreferredFromRoleAndPreleved();
      },
      immediate: true
    }
  },
  computed: {
    requiredRules() {
      return [this.required];
    }
  },
})
export default class PartyAccount extends Vue {
  party?: Third;
  tabPosition!: number;
  bicSuffix = "XXX";
  role?:OfferAssociatedParty;
  countrySelected = {
    id: "FRA",
    label: "France",
  };
  dateMask = "DD/MM/YYYY";
  statusOption = bankAccountStatusOption;
  showAccountExpense = false;
  system_uid = UON.SystemUid.odmParty;

  addExpandedItem = addExpandedItem;
  removeExpandedItem = removeExpandedItem;
  beforeFormatter(value: any) {
    // Remove non-alphanumeric characters
    return value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  }
  addAccount() {
    const account = store.getters['thirdModule/getAccountEmpty'];

    store.commit('taskModule/addAccountToParty', {
      partyUid: this.party?.resourceUid,
      account
    });
  }

  removeAccount(i: number) {
    store.dispatch('taskModule/removeAccountFromParty', {
      partyUid: this.party?.resourceUid,
      index: i
    });
  }

  get accountsModel() {
    const offer = store.state.taskModule.offer || store.state.middleOfficeModule.offer;

    const ap = offer?.associatedParties?.find(
        (p: any) => p?.associatedParty?.third?.resourceUid === this.party?.resourceUid
    );

    return ap?.associatedParty?.third?.accounts ?? [];
  }

  private syncingPreferred = false;

  private syncPreferredFromRoleAndPreleved() {
    if (this.syncingPreferred) return;
    this.syncingPreferred = true;
    try {
      const accounts = this.accountsModel;
      const roleUid = this.role?.partyAccount?.resourceUid;
      let preferredIndex = -1;
      if (roleUid) {
        preferredIndex = accounts.findIndex(
            (acc: any) => acc?.bankAccount?.resourceUid === roleUid
        );
      }
      if (preferredIndex === -1) {
        preferredIndex = accounts.findIndex(
            (acc: any) => acc?.bankAccount?.businessData?.preleved === 1
        );
      }
      accounts.forEach((acc: any, i: number) => {
        const ba = acc?.bankAccount;
        if (!ba) return;

        const shouldBePref = i === preferredIndex;

        if (Object.prototype.hasOwnProperty.call(ba, "isPreferred")) {
          if (ba.isPreferred !== shouldBePref) ba.isPreferred = shouldBePref;
        }

        if (ba.businessData && Object.prototype.hasOwnProperty.call(ba.businessData, "preleved")) {
          const target = shouldBePref ? 1 : 0;
          if (ba.businessData.preleved !== target) ba.businessData.preleved = target;
        }
      });

      if (this.role) {
        const uid =
            preferredIndex >= 0 ? accounts[preferredIndex]?.bankAccount?.resourceUid : undefined;

        const current = this.role.partyAccount?.resourceUid;
        const next = uid ?? undefined;

        if (current !== next) {
          this.role.partyAccount = { resourceUid: next };
        }
      }
    } finally {
      this.syncingPreferred = false;
    }
  }

  // Method to get bank name for a specific account
  getBankName(index: number): string {
    const account = this.accountsModel[index];
    if (account?.bankAccount?.businessData?.bankName) {
      return account.bankAccount.businessData.bankName;
    }
    
    // Fallback to offer data if available
    const party = this.offer?.businessData.parties?.[this.tabPosition];
    return party?.bankAccount?.bankName || "";
  }

  // Method to update bank name
  updateBankName(index: number, newValue: string) {
    const account = this.accountsModel[index];
    if (account?.bankAccount) {
      if (!account.bankAccount.businessData) {
        account.bankAccount.businessData = { bankName: "", preleved: 1 };
      }
      account.bankAccount.businessData.bankName = newValue;
      
      // Update the original party data
      if (this.party?.accounts?.[index]) {
        this.party.accounts[index].bankAccount.businessData = account.bankAccount.businessData;
      }
    }
  }

  getIsPreferredByAccount(account: any): boolean {
    const bankId = account?.bankAccount?.resourceUid;
    const selectedId = this.role?.partyAccount?.resourceUid;
    if (bankId && selectedId) return bankId === selectedId;
    if (!bankId) return !!account?.bankAccount?.isPreferred;
    if (!selectedId) return !!account?.bankAccount?.isPreferred;
    return false;
  }
  updatePreferred(index: number, isPreferred: boolean, row: any) {
    if (!this.party?.accounts?.[index] || !this.role) return;

    const accounts = this.party.accounts as any[];

    if (accounts.length === 1) {
      const only = accounts[0];
      const onlyBankId = only?.bankAccount?.resourceUid;

      if (only?.bankAccount && Object.prototype.hasOwnProperty.call(only.bankAccount, 'isPreferred')) {
        only.bankAccount.isPreferred = !!isPreferred;
      }
      if (
          only?.bankAccount?.businessData &&
          Object.prototype.hasOwnProperty.call(only.bankAccount.businessData, 'preleved')
      ) {
        only.bankAccount.businessData.preleved = isPreferred ? 1 : 0;
      }

      this.role.partyAccount = { resourceUid: isPreferred ? (onlyBankId ?? undefined) : undefined };
      return;
    }

    const bankId =
        row?.bankAccount?.resourceUid ??
        accounts[index]?.bankAccount?.resourceUid;

    if (isPreferred) {
      if (row?.bankAccount && Object.prototype.hasOwnProperty.call(row.bankAccount, 'isPreferred')) {
        row.bankAccount.isPreferred = true;
      }
      if (
          row?.bankAccount?.businessData &&
          Object.prototype.hasOwnProperty.call(row.bankAccount.businessData, 'preleved')
      ) {
        row.bankAccount.businessData.preleved = 1;
      }

      this.role.partyAccount = { resourceUid: bankId ?? undefined };

      accounts.forEach((acc, i) => {
        if (i !== index && acc?.bankAccount) {
          if (Object.prototype.hasOwnProperty.call(acc.bankAccount, 'isPreferred')) {
            acc.bankAccount.isPreferred = false;
          }
          if (
              acc.bankAccount.businessData &&
              Object.prototype.hasOwnProperty.call(acc.bankAccount.businessData, 'preleved')
          ) {
            acc.bankAccount.businessData.preleved = 0;
          }
        }
      });
    } else {
      const isCurrentlyPreferred =
          (bankId && this.role.partyAccount?.resourceUid === bankId) ||
          (!bankId && this.role.partyAccount?.resourceUid === undefined && !!row.bankAccount?.isPreferred);

      if (!isCurrentlyPreferred) {
        if (row?.bankAccount && Object.prototype.hasOwnProperty.call(row.bankAccount, 'isPreferred')) {
          row.bankAccount.isPreferred = false;
        }
        if (
            row?.bankAccount?.businessData &&
            Object.prototype.hasOwnProperty.call(row.bankAccount.businessData, 'preleved')
        ) {
          row.bankAccount.businessData.preleved = 0;
        }
        return;
      }

      if (row?.bankAccount && Object.prototype.hasOwnProperty.call(row.bankAccount, 'isPreferred')) {
        row.bankAccount.isPreferred = false;
      }
      if (
          row?.bankAccount?.businessData &&
          Object.prototype.hasOwnProperty.call(row.bankAccount.businessData, 'preleved')
      ) {
        row.bankAccount.businessData.preleved = 0;
      }

      this.role.partyAccount = { resourceUid: undefined };

      accounts.forEach((acc) => {
        if (acc?.bankAccount) {
          if (Object.prototype.hasOwnProperty.call(acc.bankAccount, 'isPreferred')) {
            acc.bankAccount.isPreferred = false;
          }
          if (
              acc.bankAccount.businessData &&
              Object.prototype.hasOwnProperty.call(acc.bankAccount.businessData, 'preleved')
          ) {
            acc.bankAccount.businessData.preleved = 0;
          }
        }
      });
    }
  }


  get currentRoute() {
    return useRoute().name;
  }
  
  required(value: any, message?: string) {
    return globalConfig.input_rules.required(value, message);
  }
  
  lengthMin(value: any, length: number, message?: string) {
    return globalConfig.input_rules.lengthMin(value, length, message);
  }
  
  updateBic(index: number, newValue: any) {
    const size = newValue.length;
    if (size === 8) {
      newValue = newValue + this.bicSuffix;
    }
    const account = this.accountsModel[index];
    if (account?.bankAccount) {
      account.bankAccount.bic = newValue;
      
      // Update the original party data
      if (this.party?.accounts?.[index]) {
        this.party.accounts[index].bankAccount.bic = newValue;
      }
    }
  }
  
  formatDate(date: string) {
    return formatDateLocale(date, this.dateMask, "fr", "YYYY/MM/DD");
  }
  
  formatDateUntil(value: any) {
    const formattedDate = value.bankAccount.validity.until.includes("/")
      ? formatDateLocale(
        value.bankAccount.validity.until,
        "DD/MM/YYYY",
        "fr",
        "DD/MM/YYYY"
      )
      : formatDateLocale(
        value.bankAccount.validity.until,
        "DDMMYYYY",
        "fr",
        "DD/MM/YYYY"
      );
    
    value.bankAccount.validity.until = formattedDate;
    
    // Update the original party data
    const index = this.accountsModel.findIndex(acc => acc.resourceUid === value.resourceUid);
    if (index !== -1 && this.party?.accounts?.[index] && this.party.accounts[index].bankAccount && this.party.accounts[index].bankAccount.validity) {
      this.party.accounts[index].bankAccount.validity!.until = formattedDate;
    }
  }
  
  formatDateFrom(value: any) {
    const formattedDate = value.bankAccount.validity.from.includes("/")
      ? formatDateLocale(
        value.bankAccount.validity.from,
        "DD/MM/YYYY",
        "fr",
        "DD/MM/YYYY"
      )
      : formatDateLocale(value.bankAccount.validity.from, "DDMMYYYY", "fr", "DD/MM/YYYY");
    
    value.bankAccount.validity.from = formattedDate;
    
    // Update the original party data
    const index = this.accountsModel.findIndex(acc => acc.resourceUid === value.resourceUid);
    if (index !== -1 && this.party?.accounts?.[index] && this.party.accounts[index].bankAccount && this.party.accounts[index].bankAccount.validity) {
      this.party.accounts[index].bankAccount.validity!.from = formattedDate;
    }
  }
  
  async validateIban(value: string) {
    const newValue = value?.split(" ").join("");
    const response = await upsertWithDataMessageReturn(
      this.system_uid,
      newValue,
      "validateIban"
    );
    if (!!response && !!response.message && response.message !== "IBAN is correct") {
      return response.message;
    }
  }

  isMo() {
    return this.currentRoute !== 'TaskDetail';
  }
  
  get offer() {
    return this.isMo()
      ? store.state.middleOfficeModule?.offer
      : store.state.taskModule?.offer;
  }

  get FR() {
    return {
      days: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
      daysShort: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'],
      months: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
      monthsShort: ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc']
    }
  }

  mounted() {
    const partyUid = this.party?.resourceUid;
    if (partyUid) {
      const emptyAccount = store.getters['thirdModule/getAccountEmpty'];
      store.commit('taskModule/ensurePartyHasAtLeastOneAccount', { partyUid, emptyAccount });
    }
  }
}
